<template>
  <OverlaySpinner :overlay="true" v-if="isLoading" />
  <div class="portfolio-zoom-wrapper">
  <div class="portfolio-view-container">
    <div class="breadcrumb-container">Portfolio</div>
    <div class="dashboard-header">
      <div class="section-title-container">Dashboard</div>
    </div>
    <!-- 3 Equal Width Charts -->
    <div class="dashboard-three-column">
      <!-- Left: Map -->
      <div class="dashboard-tile map-tile">
        <div id="myDataMap" style="height: 100%"></div>
      </div>
      
      <!-- Middle: Projects by Asset Class -->
      <div class="dashboard-tile doughnut-tile" v-if="isDoughnutChartDataLoaded">
        <div class="tile-header">
          <div class="chart-title">Projects by Asset Class</div>
          <div class="chart-badge">{{ totalProjects }}</div>
        </div>
        <div class="doughnut-container">
          <div class="doughnut-chart-wrapper">
            <Doughnut :height=220 :width=220 :data="doughnutData" :options="doughnutOptions" />
          </div>
          <div class="doughnut-chart-legend">
            <div class="residential-legend legend-item">
              <div class="legend-label">Residential</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[0] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(residentialTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="office-legend legend-item">
              <div class="legend-label">Commercial</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[1] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(commercialTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="industrial-legend legend-item">
              <div class="legend-label">Industrial</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[2] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(industrialTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="hospitality-legend legend-item">
              <div class="legend-label">Hospitality</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[3] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(hospitalityTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="retail-legend legend-item">
              <div class="legend-label">Retail</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[4] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(retailTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="civic-cultural-legend legend-item">
              <div class="legend-label">Civic & Cultural</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[5] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(civicCulturalTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="mixed-use-legend legend-item">
              <div class="legend-label">Mixed Use</div>
              <div class="detail-container">
                <div class="percentage">{{ doughnutChartPercentage[6] }}</div>
                <div class="vertical-divider"></div>
                <div class="amount">{{ kMillionsWithDecimalsFormat(mixedUseTotalRevisedBudget) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modern-summary-grid">
          <div class="summary-item">
            <div class="summary-label">Total Projects:</div>
            <div class="summary-value">{{ totalProjects }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total GSF:</div>
            <div class="summary-value">{{ totalGSF.toLocaleString() }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total States:</div>
            <div class="summary-value">{{ totalStates }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Clients:</div>
            <div class="summary-value">{{ totalClients }}</div>
          </div>
        </div>
      </div>

      <!-- Right: Variance Summary (3 charts - Budget/Schedule top, Project Starts bottom) -->
      <div class="dashboard-tile variance-and-starts-tile">
        <div class="tile-header compact-header minimal-header">Variance Summary</div>
        <div class="variance-top-section">
          <div class="variance-chart budget-chart">
            <div class="variance-subtitle">Budget</div>
            <div class="chart-container">
              <Bar :data="budgetVarianceStackedBarData" :options="budgetVarianceStackedBarOptions" />
            </div>
          </div>
          <div class="variance-chart schedule-chart">
            <div class="variance-subtitle">Schedule</div>
            <div class="chart-container">
              <div class="coming-soon-message" style="display: flex; align-items: center; justify-content: center; height: 100%; color: #6b7280; font-size: 16px; font-weight: 500;">
                Coming Soon
              </div>
            </div>
          </div>
        </div>
        
        <div class="project-starts-section">
          <div class="variance-subtitle">Project Starts</div>
          <div class="chart-container project-starts-chart">
            <Line :data="projectStartsLineChartData" :options="projectStartsLineChartOptions" />
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Projects Header -->
    <div class="projects-header">
      <div class="section-title-container">Projects</div>
      <div class="section-subtitle-container">
        <div>View Active Projects</div>
        <div class="toggle-container" :class="{ 'active': isActive }" @click="toggleActive">
          <div class="circle" :class="{ 'move-right': isActive }"></div>
        </div>
      </div>
      <div class="expand-action-button" @click="toggleColumns">
        <div v-if="isGridExpanded" class="action-name">Collapse Fields</div>
        <div v-else class="action-name">Expand Fields</div>
        <div v-if="isGridExpanded" class="cssCircle minusSign">&#45;</div>
        <div v-else class="cssCircle plusSign">&#43;</div>
      </div>
    </div>
    
    <!-- Enhanced Data Table -->
    <div class="enhanced-table-container">
      <ag-grid 
        style="min-height:300px" 
        :row-height="75" 
        :auth-token="authToken" 
        :full-scroll="true" 
        :override-width="true"
        :row-data="rowData" 
        :column-defs="colDefs" 
        :key="rowData.length" 
        :columns-to-toggle="columnsToToggle" 
        @setChartData="setChartData" 
        ref="agGridRef"
      />
    </div>
  </div>
  </div>
</template>

<script setup>
/* eslint no-undef: 0 */
import {computed, onMounted, reactive, ref} from 'vue';
import { useAuthStore } from '@/stores/auth_store';
import { useErrorStore } from "@/stores/error_store";
import { axiosQuery, currencyFormat, getImageUrl, kMillionsFormat, kMillionsWithDecimalsFormat, numberFormat, percentFormat, standardizeLabelsLength } from "@/assets/js/utils";
import AgGrid from "@/components/AgGrid.vue";
import OverlaySpinner from "@/components/helpers/OverlaySpinner.vue"
import { Bar, Doughnut, Line } from "vue-chartjs";
import {
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  CategoryScale,
  LinearScale,
  Colors,
  Filler,
  Legend,
  Title,
  Tooltip,
  Chart as ChartJS,
} from "chart.js";
import { doughnutMiddleTextPlugin, standardDoughnutChartJson } from "@/assets/js/chart_config";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { loadGoogleMapsAPI } from '@/main.js';
import { fetchImage } from "@/assets/js/image_utils";

// General Settings
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
  doughnutMiddleTextPlugin(),
  ChartDataLabels
);
const apiBase = process.env.VUE_APP_ENV_API_BASE || "/api/v1/";
let authToken = reactive({});
const authStore = useAuthStore();
const errorStore = useErrorStore();
const isDataLoaded = computed(() => {
  return isBudgetVarianceDataFetch.value && isScheduleVarianceDataFetch.value;
});
const isBudgetVarianceDataFetch = ref(false);
const isScheduleVarianceDataFetch = ref(false);
let isActive = ref(false);

// Company data refs for proper name resolution (like directory page)
let investorSponsors = ref([]);
let consultants = ref([]);
let contractors = ref([]);

async function toggleActive() {
  isActive.value = !isActive.value;
  isLoading.value = true;
  if(isActive.value) {
    const currentDate = new Date();
    rowData.value = rowData.value.filter(row => {
      const endDate = new Date(row.actual_end_date);
      return endDate > currentDate;
    });
    activeGridData.value = rowData.value;
  } else {
    const selectedIds = await callAllProjects();
    await getDonutChartData(selectedIds);
  }

  await drawDonutChart()
  await drawVarianceCharts()
  await drawAreaChart()
  isLoading.value = false;

}

// ❌ REMOVED loadCompanyData() - No longer needed!
// Portfolio materialized view endpoint already includes all consultant, contractor, and investor data
// This was causing ERR_INSUFFICIENT_RESOURCES due to too many simultaneous requests

// Map Settings
const isGoogleMapsMounted = ref(false);
let map;
let markers = []
let infoWindows = []
let circleCenterLocation = { lat: 40.7740, lng: -73.9710 }; // New York City to start

function loadGoogleMaps() {
  if (typeof google !== 'undefined' && google.maps) {
    try {
      const mapOptions = {
        center: circleCenterLocation,
        zoom: 12,
        mapId: 'myDataMap', // Note: As of 12/12, map ids need to be different for each place where we're using google maps to avoid confusion / errors on containers
        mapTypeId: 'terrain',
      };

      // Make sure the element exists before trying to create the map
      const mapElement = document.getElementById('myDataMap');
      if (!mapElement) {
        console.error('Map element not found');
        return;
      }

      map = new google.maps.Map(mapElement, mapOptions);
      isGoogleMapsMounted.value = true;
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
      errorStore.setError('Google Map Error', 'Error initializing Google Maps: ' + error.message, 'error');
    }
  } else {
    console.error('Google Maps API not loaded properly');
    errorStore.setError('Google Map Error', 'Google Maps API not loaded properly', 'error');
  }
}

const navigateToProjectSummary = (project) => {
  if (project && project.id) {
    window.open(`/portfolio/project-summary/${project.id}`, '_blank');
  } else {
    errorStore.setError('Error', 'An error occurred. Project ID not found', 'error');
  }
};

let currentInfoWindow = null;
let cachedMarkerContent = null;

// Optimized marker content creation - parse SVG once and reuse
function getMarkerContent() {
  if (cachedMarkerContent) {
    return cachedMarkerContent.cloneNode(true);
  }
  
  const parser = new DOMParser();
  const pinSvgString =
    '<svg xmlns="http://www.w3.org/2000/svg" width="5.6444445mm" height="9.847393mm" viewBox="0 0 20 34.892337" version="1.1" >\n' +
    '  <g transform="translate(-814.59595,-274.38623)">\n' +
    '    <g transform="matrix(1.1855854,0,0,1.1855854,-151.17715,-57.3976)">\n' +
    '      <path d="m 817.11249,282.97118 c -1.25816,1.34277 -2.04623,3.29881 -2.01563,5.13867 0.0639,3.84476 1.79693,5.3002 4.56836,10.59179 0.99832,2.32851 2.04027,4.79237 3.03125,8.87305 0.13772,0.60193 0.27203,1.16104 0.33416,1.20948 0.0621,0.0485 0.19644,-0.51262 0.33416,-1.11455 0.99098,-4.08068 2.03293,-6.54258 3.03125,-8.87109 2.77143,-5.29159 4.50444,-6.74704 4.56836,-10.5918 0.0306,-1.83986 -0.75942,-3.79785 -2.01758,-5.14062 -1.43724,-1.53389 -3.60504,-2.66908 -5.91619,-2.71655 -2.31115,-0.0475 -4.4809,1.08773 -5.91814,2.62162 z" style="display:inline;opacity:1;fill:#4A5FD5;fill-opacity:1;stroke:#3b73a8;stroke-width:1;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1"/>\n' +
    '      <circle r="3.0355" cy="288.25278" cx="823.03064" style="display:inline;opacity:1;fill:#fff;fill-opacity:1;stroke-width:0"/>\n' +
    '    </g>\n' +
    '  </g>\n' +
    '</svg>\n';
  
  cachedMarkerContent = parser.parseFromString(pinSvgString, "image/svg+xml").documentElement;
  return cachedMarkerContent.cloneNode(true);
}

function updateMarker(project) {
  // Check if map is available before proceeding
  if (!map || !isGoogleMapsMounted.value) {
    return;
  }

  // Enhanced coordinate validation with more specific checks
  const lat = parseFloat(project.latitude);
  const lng = parseFloat(project.longitude);

  if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0 || 
      Math.abs(lat) > 90 || Math.abs(lng) > 180 ||
      !project.latitude || !project.longitude) {
    // Silently skip invalid coordinates to avoid console spam
    return;
  }

  const location = { lat, lng };

  if (!google.maps.marker) { loadGoogleMaps() }
  
  // Use pre-created marker content to improve performance
  const markerContent = getMarkerContent();
  
  const marker = new google.maps.marker.AdvancedMarkerElement({
    position: location,
    map: map,
    content: markerContent
  });

  const projectImageUrl = getPrimaryImageUrl(project);
  const hasRealImage = projectImageUrl && !projectImageUrl.includes('data:image/svg');
  console.log('🖼️ Marker image for project', project.id, ':', projectImageUrl);
  
  const imageHtml = hasRealImage 
    ? `<div style="flex-shrink: 0; margin-right: 10px;">
         <img id="project-image-${project.id}" 
              src="${projectImageUrl}"
              style="width:100px; height:75px; object-fit: cover; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s; border-radius: 4px; background: #5a8fc4;" 
              onmouseover="this.style.borderColor='#ffffff'" 
              onmouseout="this.style.borderColor='transparent'" 
              title="Click to view project details"
              onerror="this.style.display='none';">
       </div>`
    : '';
  
  const infoWindow = new google.maps.InfoWindow({
    content: `<div style="background: #3b73a8; color: #fff; font-size: 12px; display: flex; align-items: flex-start; padding: 8px; width: 320px; min-height: 120px;">
                ${imageHtml}
                <div style="flex: 1; min-width: 0;">
                  <div style="margin-bottom: 8px;">
                    <a id="project-link-${project.id}" href="javascript:void(0);" 
                       style="color: #ffffff; text-decoration: underline; cursor: pointer; font-weight: bold; font-size: 13px; line-height: 1.2;">
                      ${project.name || project.address_line_1}
                    </a>
                  </div>
                  <div style="display: grid; grid-template-columns: auto 1fr; gap: 4px 8px; font-size: 11px;">
                    <div style="color: #ccc;">Address:</div>
                    <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${project.address_line_1 || '-'}</div>
                    
                    <div style="color: #ccc;">Total GSF:</div>
                    <div>${project.total_gsf ? numberFormat(project.total_gsf, 0) : '-'}</div>
                    
                    <div style="color: #ccc;">Units:</div>
                    <div>${project.total_num_units ? numberFormat(project.total_num_units, 0) : '-'}</div>
                    
                    <div style="color: #ccc;">Budget:</div>
                    <div style="font-weight: bold;">${project.total_current_budget ? currencyFormat(project.total_current_budget) : '-'}</div>
                    
                    <div style="color: #ccc;">Type:</div>
                    <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${project.project_construction_types_s ? project.project_construction_types_s[0] : '-'}</div>
                    
                    <div style="color: #ccc;">Status:</div>
                    <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${project.project_status || '-'}</div>
                  </div>
                </div>
              </div>`,
  });

  infoWindow.addListener('domready', () => {
    const linkElement = document.getElementById(`project-link-${project.id}`);
    if (linkElement) {
      linkElement.addEventListener('click', () => navigateToProjectSummary(project));
    }
    
    // Image src is now set directly in HTML, just add click handler
    const imgElement = document.getElementById(`project-image-${project.id}`);
    if (imgElement && hasRealImage) {
      imgElement.addEventListener('click', () => navigateToProjectSummary(project));
    }
  });

  // 🔥 FIX: Show full detailed popup on hover
  let hoverTimeout;

  // Add hover events directly to the marker content
  markerContent.addEventListener('mouseenter', () => {
    // Only show hover if no info window is currently open
    if (!currentInfoWindow) {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(() => {
        // Open the full detailed info window on hover
        infoWindow.open({
          anchor: marker,
          map,
          shouldFocus: false,
        });
        currentInfoWindow = infoWindow;
      }, 300); // Short delay for better UX
    }
  });

  markerContent.addEventListener('mouseleave', () => {
    clearTimeout(hoverTimeout);
    // Don't close if user moves to the info window itself
    // Info window will stay open until user moves mouse away completely
  });

  markerContent.addEventListener('click', () => {
    clearTimeout(hoverTimeout);
    
    // 🔥 FIX: Click keeps popup open (sticky) or closes it if already open
    if (currentInfoWindow === infoWindow) {
      // Close if already open
      currentInfoWindow.close();
      currentInfoWindow = null;
    } else {
      // Close any other open info window
      if (currentInfoWindow) {
        currentInfoWindow.close();
      }
      
      // Open this marker's info window and make it sticky
      infoWindow.open({
        anchor: marker,
        map,
        shouldFocus: true,
      });

      currentInfoWindow = infoWindow;
    }
  });

  markers.push(marker);
  infoWindows.push(infoWindow);
}

// Helper function to get primary image URL from project's documents array (consistent with CompSummary)
const getPrimaryImageUrl = (project) => {
  // Skip the old default image if present
  if (project.image_url && project.image_url !== '/img/property1.jpg') {
    return project.image_url;
  }
  
  // Look for primary image in documents array
  if (project.documents && Array.isArray(project.documents)) {
    const primaryDoc = project.documents.find(doc => doc.is_primary === true);
    if (primaryDoc && primaryDoc.id) {
      // Use ABSOLUTE URL for Google Maps info windows (relative URLs don't work)
      const imageUrl = window.location.hostname === 'localhost' 
        ? `http://localhost:3000/api/v1/documents/${primaryDoc.id}/file`
        : `${window.location.protocol}//${window.location.host}/api/v1/documents/${primaryDoc.id}/file`;
      return imageUrl;
    }
  }
  
  // Default placeholder if no image found (use data URL to avoid external dependency)
  return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
};

const handleImageError = (event) => {
  console.log('🖼️ Image load error:', event.target.src);
  event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
};

async function loadMarkers(data) {
  // Clear the old markers, info windows, and circle
  infoWindows.forEach(infoWindow => infoWindow.close());
  markers.forEach(marker => marker.setMap(null));

  // Reset the values
  markers = [];
  infoWindows = [];

  // Filter out projects with invalid coordinates upfront to improve performance
  const validProjects = data.filter(project => {
    const lat = parseFloat(project.latitude);
    const lng = parseFloat(project.longitude);
    return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 && 
           Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
           project.latitude && project.longitude;
  });

  if (validProjects.length === 0) {
    console.log('No projects with valid coordinates found');
    return;
  }

  // Process markers in batches to prevent timeout violations
  const batchSize = 25;
  let processedCount = 0;
  
  for (let i = 0; i < validProjects.length; i += batchSize) {
    const batch = validProjects.slice(i, i + batchSize);
    
    // Process each batch with a small delay to prevent UI blocking
    await new Promise(resolve => {
      setTimeout(() => {
        batch.forEach(project => updateMarker(project));
        processedCount += batch.length;
        resolve();
      }, 0);
    });
  }

  // After processing all markers, check if any were added
  if (markers.length > 0) {
    console.log('Markers added:', markers.length);
  } else if (map && map.getCenter) { // Add null check for map object
    // If not, add a marker at the center of the map
    const centerLat = map.getCenter().lat();
    const centerLng = map.getCenter().lng();
    const mapCenterLocation = new google.maps.LatLng(centerLat, centerLng);
    // If there are no markers, add a marker at the searched location with "No available projects here"
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: mapCenterLocation,
      map: map,
    });
  
    const infoWindow = new google.maps.InfoWindow({
      content: `<h1>No available projects here</h1>`,
    });
  
    marker.addListener('click', () => {
      infoWindow.open({
        anchor: marker,
        map,
        shouldFocus: false,
      });
    });
  
    markers.push(marker);
    infoWindows.push(infoWindow);
  }
}

let activeGridData = ref({})
function setChartData(event) {
  // get non-filtered ids
  let activeIds = event.map(row => row.data.id)
  activeGridData.value = rowData.value?.filter(row =>
    activeIds.includes(row.id)
  );
  // Only load markers if the map is initialized
  if (map && isGoogleMapsMounted.value) {
    loadMarkers(activeGridData.value); // Update markers on the map
  }
  drawDonutChart()
  drawVarianceCharts()
  drawAreaChart()
}

// Doughnut Chart Settings
//TODO: Need to update these values
// General Variables
const isDoughnutChartDataLoaded = ref(false);

let residentialTotalOriginalBudget = ref(0);
let residentialTotalRevisedBudget = ref(0);
let commercialTotalOriginalBudget = ref(0);
let commercialTotalRevisedBudget = ref(0);
let hospitalityTotalOriginalBudget = ref(0);
let hospitalityTotalRevisedBudget = ref(0);
let industrialTotalOriginalBudget = ref(0);
let industrialTotalRevisedBudget = ref(0);
let retailTotalOriginalBudget = ref(0);
let retailTotalRevisedBudget = ref(0);
let civicCulturalTotalOriginalBudget = ref(0);
let civicCulturalTotalRevisedBudget = ref(0);
let mixedUseTotalOriginalBudget = ref(0);
let mixedUseTotalRevisedBudget = ref(0);

let residentialTotalVariance = ref(0);
let commercialTotalVariance = ref(0);
let hospitalityTotalVariance = ref(0);
let industrialTotalVariance = ref(0);
let retailTotalVariance = ref(0);
let civicCulturalTotalVariance = ref(0);
let residentialVariancePercent = ref(0);
let industrialVariancePercent = ref(0);
let commercialVariancePercent = ref(0);
let hospitalityVariancePercent = ref(0);
let retailVariancePercent = ref(0);
let civicCulturalVariancePercent = ref(0);

let totalRevisedBudget = computed(() => {
  return residentialTotalRevisedBudget.value +
    commercialTotalRevisedBudget.value +
    industrialTotalRevisedBudget.value +
    hospitalityTotalRevisedBudget.value +
    retailTotalRevisedBudget.value +
    civicCulturalTotalRevisedBudget.value +
    mixedUseTotalRevisedBudget.value
})

let totalProjects = computed(() => {
  return activeGridData?.value.length
})

let totalGSF = computed(() => {
  return activeGridData?.value.reduce((acc, val) => {
    return acc + val.total_gsf
  }, 0).toLocaleString()
})

let totalStates = computed(() => {
  const uniqueStates = activeGridData?.value.reduce((acc, val) => {
    acc.add(val.state);
    return acc;
  }, new Set());
  return uniqueStates.size;
});

let totalClients = computed(() => {
  let lenders = []
  activeGridData?.value?.forEach(acc => {
    if (!lenders.includes(acc?.primary_project_lender)) {
      lenders.push(acc?.primary_project_lender)
    }
  })

  return lenders.length
})

// Middle Text
let doughnutLabel = computed(() => {
  return 'Total'
})
// Middle Value
let doughnutSummaryValue = computed(() => {
  return kMillionsWithDecimalsFormat(totalRevisedBudget.value)
})

let doughnutChartJsonConfig = standardDoughnutChartJson({
  doughnutTitle: "Projects By Asset Class",
})
let doughnutChartPercentage = ref([]);
let doughnutChartData = ref([])
// TODO - Syd : This should potentially be fetched as Asset Categories from Codes table
const doughnutData = computed(() => {
  return {
    labels: ["Residential", "Commercial", "Industrial", "Hospitality", "Retail", "Civic & Cultural", "Mixed Use"],
    datasets: [
      {
        backgroundColor: [
          "#52596D", // $darkGray1
          "#4A5FD5", // $brightBlue1
          "#4A8FD0", // $mediumBlue1
          "#8A92A9", // $mediumGray1
          "#d3d3d3", // assigning to light gray for now
          "#6ED0CD", // assigning to $brightTeal1 for now
                      "#9B59B6" // Purple for Mixed Use
        ],
        data: doughnutChartData.value
      },
    ],
  };
});

const doughnutOptions = computed(() => {
  let json = doughnutChartJsonConfig
  json.cutout = 85
  json.plugins.legend.display = false
  json.plugins.doughnutMiddleTextPlugin.middleTextLabel = doughnutLabel.value;
  json.plugins.doughnutMiddleTextPlugin.middleTextValue = doughnutSummaryValue.value;
  json.plugins.doughnutMiddleTextPlugin.fontSize = 18;
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      let actual = 0;
      if (context.label?.includes("Residential")) {
        actual = currencyFormat(residentialTotalRevisedBudget.value)
      } else if (context.label?.includes("Commercial")) {
        actual = currencyFormat(commercialTotalRevisedBudget.value)
      } else if (context.label?.includes("Industrial")) {
        actual = currencyFormat(industrialTotalRevisedBudget.value)
      } else if (context.label?.includes("Hospitality")) {
        actual = currencyFormat(hospitalityTotalRevisedBudget.value)
      } else if (context.label?.includes("Retail")) {
        actual = currencyFormat(retailTotalRevisedBudget.value)
      } else if (context.label?.includes("Civic & Cultural")) {
        actual = currencyFormat(civicCulturalTotalRevisedBudget.value)
      }
      return actual;
    },
    footer: function (context) {
      let variancePerc = 0;
      let varianceVal = 0;
      if (context[0].label?.includes("Residential")) {
        varianceVal = kMillionsFormat(residentialTotalVariance.value);
        variancePerc = percentFormat(residentialVariancePercent.value);
      } else if (context[0].label?.includes("Commercial")) {
        varianceVal = kMillionsFormat(commercialTotalVariance.value);
        variancePerc = percentFormat(commercialVariancePercent.value);
      } else if (context[0].label?.includes("Industrial")) {
        varianceVal = kMillionsFormat(industrialTotalVariance.value);
        variancePerc = percentFormat(industrialVariancePercent.value);
      } else if (context[0].label?.includes("Hospitality")) {
        varianceVal = kMillionsFormat(hospitalityTotalVariance.value);
        variancePerc = percentFormat(hospitalityVariancePercent.value);
      } else if (context[0].label?.includes("Retail")) {
        varianceVal = kMillionsFormat(retailTotalVariance.value);
        variancePerc = percentFormat(retailVariancePercent.value);
      } else if (context[0].label?.includes("Civic & Cultural")) {
        varianceVal = kMillionsFormat(civicCulturalTotalVariance.value);
        variancePerc = percentFormat(civicCulturalVariancePercent.value);
      }
      return `Variance: ${varianceVal} (${variancePerc})`
    },
  }
  json.responsive = true;
  return json
});

// Budget Variance and Schedule Variance Bar Chart Settings
// General Settings
let bvStackedBarLabels = ref([]);
let bvBudgetVarianceArr = ref([]);
let svStackedBarLabels = ref([]);
let svScheduleVarianceArr = ref([]);
const bvMaxValue = ref(0); // Set the maximum value for the x-axis of budget variance
const svMaxValue = ref(0); // Set the maximum value for the x-axis of schedule variance
let svScheduleVarianceArrZeroValues = ref(false);

const budgetVarianceStackedBarData = computed(() => {
  return {
    labels: standardizeLabelsLength(bvStackedBarLabels.value),
    datasets: [
      {
        label: 'Budget Variance',
        backgroundColor: "#84CADE", // $lightBlue1
        data: bvBudgetVarianceArr.value,
        barThickness: 12, // Fixed bar thickness for crisp rendering
        borderRadius: 2, // Slight rounding for modern look
      }
    ]
  }
});

const budgetVarianceStackedBarOptions = computed(() => {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 2, // Higher resolution for crisp rendering
    animation: {
      duration: 0 // Disable animation for crisper rendering
    },
    layout: {
      padding: {
        top: 15,
      }
    },
    scales: {
      x: {
        position: 'top',
        max: 1,
        grid: {
          drawBorder: true,
          drawOnChartArea: true,
          color: function (context) {
            if (context.tick.value === 0) {
              return '#4A5FD5';
            }
            return 'rgba(0, 0, 0, 0.1)';
          },
          lineWidth: function (context) {
            if (context.tick.value === 0) {
              return 2;
            }
            return 1;
          }
        },
        ticks: {
          font: {
            size: 10,
          },
          callback: function(value) {
            if (!value) {
              return value
            }
            return `${Math.round(parseFloat(value).toFixed(2) * 100)}%`;
          },
          maxTicksLimit: 5
        },
      },
      y: {
        ticks: {
            font: {
                size: 10,
            }
        },
      },
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        anchor: 'end',
        clamp: true,
        align: function (context) {
          var value = context.dataset.data[context.dataIndex];
          return value < 0 || value > 1 ? 'e' : 'end';
        },
        color: '#000000',
        font: {
          weight: 'normal',
          size: 10,
        },
        formatter: function (value) {
          if (!value) {
            return value
          }
          return `${Math.round(parseFloat(value).toFixed(2) * 100)}%`;
        }
      },
    }
  }
})

const scheduleVarianceStackedBarData = computed(() => {
  return {
    labels: standardizeLabelsLength(svStackedBarLabels.value),
    datasets: [
      {
        label: 'Schedule Variance',
        backgroundColor: "#84CADE", // $lightBlue1
        data: svScheduleVarianceArr.value,
      }
    ]
  }
});

const scheduleVarianceStackedBarOptions = computed(() => {
  return {
    indexAxis: 'y',
    responsive: true,
    layout: {
      padding: {
        top: 15,
      }
    },
    scales: {
      x: {
        position: 'top',
        max: svMaxValue.value * 1.5,
        grid: {
          drawBorder: true,
          drawOnChartArea: true,
          color: function (context) {
            if (context.tick.value === 0) {
              return '#4A5FD5';
            }
            return 'rgba(0, 0, 0, 0.1)';
          },
          lineWidth: function (context) {
            if (context.tick.value === 0) {
              return 2;
            }
            return 1;
          }
        },
        ticks: {
            font: {
                size: 10,
            },
            maxTicksLimit: 5
        },
      },
      y: {
        ticks: {
            font: {
                size: 10,
            },
        },
      },
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        anchor: 'end',
        clamp: true,
        align: function (context) {
          var value = context.dataset.data[context.dataIndex];
          return value < 0 ? 'start' : 'end';
        },
        color: '#000000',
        font: {
          weight: 'normal',
          size: 10,
        },
        formatter: function (value) {
          return `${parseFloat(value).toFixed(1)} Mo`;
        }
      }
    }
  }
})

// Project Starts Line Chart Settings
// General Settings
let projectStartsLineLabels = ref([]);
let projectStartsLineData = ref([]);

const projectStartsLineChartData = computed(() => {
  return {
    labels: projectStartsLineLabels.value,
    datasets: [
      {
        label: 'Project Starts',
        backgroundColor: "#A0D1FF", // not in our color palette but it's name is ButterflyBlue
        data: projectStartsLineData.value,
        fill: true,
        borderColor: "#3CA1FB", // not in our color palette but it's name is ButterflyBlue
        pointRadius: 0, // Remove dots
        tension: 0.4, // Smooth bezier curves
        borderWidth: 2
      }
    ]
  }
});

const projectStartsLineChartOptions = computed(() => {
  return {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false, // Allow chart to fill container
    layout: {
      padding: {
        top: 20, // Reduced padding to use more space
        left: 10,
        right: 10,
        bottom: 10
      }
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        anchor: 'top',
        align: 'end',
        color: '#000000',
        font: {
          weight: 'normal',
          size: 10
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          font: {
            size: 10
          }
        }
      },
      y: {
        type: 'linear',
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          stepSize: 5,
          font: {
            size: 10
          },
          callback: function (value) {
            return value.toFixed(0);
          }
        }
      }
    }
  }
})

// AG-Grid Settings
let rowData = ref([]);
let colDefs = ref([
  {
    headerName: '', 
    field: 'thumbnail', 
    resizable: false, 
    sortable: false, 
    filter: false, 
    minWidth: 60, 
    maxWidth: 60,
    cellRenderer: (data) => {
      const imageUrl = getPrimaryImageUrl(data.data);
      const notes = data.data.notes || '';
      const projectName = data.data.name || 'Project';
      return `<div style="position: relative; width: 40px; height: 40px; overflow: hidden;">
                <img src="${imageUrl}" 
                     style="width: 40px; height: 40px; border-radius: 4px; object-fit: cover; transition: all 0.3s ease; cursor: pointer; position: relative;" 
                     onmouseover="
                       this.style.transform='scale(3)'; 
                       this.style.zIndex='99999'; 
                       this.style.boxShadow='0 12px 32px rgba(0,0,0,0.4)'; 
                       this.style.borderRadius='12px'; 
                       this.parentElement.style.zIndex='99999';
                       if ('${notes}'.trim()) {
                         const tooltip = document.createElement('div');
                         tooltip.id = 'img-tooltip-${data.data.id}';
                         tooltip.innerHTML = '<strong>${projectName}</strong><br/>${notes}';
                         tooltip.style.cssText = 'position: absolute; top: 130px; left: -100px; width: 250px; background: rgba(0,0,0,0.9); color: white; padding: 8px 12px; border-radius: 8px; font-size: 12px; z-index: 100000; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';
                         this.parentElement.appendChild(tooltip);
                       }
                     " 
                     onmouseout="
                       this.style.transform='scale(1)'; 
                       this.style.zIndex='1'; 
                       this.style.boxShadow='none'; 
                       this.style.borderRadius='4px'; 
                       this.parentElement.style.zIndex='1';
                       const tooltip = document.getElementById('img-tooltip-${data.data.id}');
                       if (tooltip) tooltip.remove();
                     "
                     onerror="if(this.src !== '/img/property1.jpg') { this.src='/img/property1.jpg'; } else { this.style.display='none'; }" 
                     alt="Project ${data.data.id}"/>
              </div>`;
    }
  },
  {
    headerName: 'Project Name', 
    field: 'name', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 180,
    cellRenderer: (data) => {
      const route = `/portfolio/project-summary/${data.data.id}`;
      const projectName = data.value || 'Unnamed Project';
      const address = data.data.address_line_1 || '';
      return `<div style="display: flex; flex-direction: column; gap: 4px; width: 100%; height: 100%; justify-content: center; padding-left: 12px;">
                <a href='${route}' target='_blank' style="font-weight: 600; color: #2563eb; text-decoration: none; font-size: 13px; line-height: 1.3; display: block;">${projectName}</a>
                ${address ? `<small style="color: #6b7280; font-size: 11px; line-height: 1.2; display: block;">${address}</small>` : ''}
              </div>`;
    }
  },
  { 
    headerName: 'Budget', 
    field: 'total_current_budget', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 100, 
    type: 'rightAligned', 
    valueFormatter: params => params.value ? currencyFormat(params.value, 0) : '-',
    cellStyle: { fontWeight: '600', color: '#059669', fontSize: '12px' }
  },
  {
    headerName: 'Budget Variance', 
    field: 'budgetVariance', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 90,
    type: 'rightAligned',
    cellStyle: params => {
      const variance = parseFloat(params.value) || 0;
      const absVariance = Math.abs(variance);
      
      if (variance === 0) {
        return {
          background: 'linear-gradient(to right, transparent 49%, #d1d5db 49%, #d1d5db 51%, transparent 51%)',
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'right',
          paddingRight: '8px'
        };
      }
      
      const isPositive = variance > 0;
      let gradientColor = '#60a5fa'; // Light blue to match user's mockup
      let barWidth = Math.min(absVariance * 2, 40); // Scale width
      
      if (isPositive) {
        return {
          background: `linear-gradient(to right, transparent 49%, #d1d5db 49%, #d1d5db 51%, #bfdbfe 51%, ${gradientColor} ${51 + barWidth}%, transparent ${51 + barWidth}%)`,
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'right',
          paddingRight: '8px'
        };
      } else {
        return {
          background: `linear-gradient(to right, transparent ${49 - barWidth}%, ${gradientColor} ${49 - barWidth}%, #bfdbfe 49%, #d1d5db 49%, #d1d5db 51%, transparent 51%)`,
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'right',
          paddingRight: '8px'
        };
      }
    },
    valueFormatter: params => {
      const variance = parseFloat(params.value) || 0;
      return variance === 0 ? '0%' : (variance > 0 ? '+' : '') + variance.toFixed(1) + '%';
    }
  },
  { 
    headerName: 'Planned Duration', 
    field: 'plannedProjectMonths', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 90,
    type: 'rightAligned',
    valueFormatter: params => {
      const months = parseInt(params.value) || 0;
      if (months === 0) return '-';
      return `${months}`;
    },
    cellStyle: { fontSize: '12px', color: '#374151', fontWeight: '500' }
  },
  { 
    headerName: 'Schedule Variance', 
    field: 'scheduleVariance', 
    hide: true, // HIDDEN - not ready for display
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 100,
    type: 'rightAligned',
    cellStyle: params => {
      const variance = parseFloat(params.value) || 0;
      const absVariance = Math.abs(variance);
      
      if (variance === 0) {
        return {
          background: 'linear-gradient(to right, transparent 49%, #d1d5db 49%, #d1d5db 51%, transparent 51%)',
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'right',
          paddingRight: '8px'
        };
      }
      
      const isPositive = variance > 0;
      let gradientColor = '#60a5fa'; // Light blue to match user's mockup
      let barWidth = Math.min(absVariance * 1.5, 35); // Scale width for schedule variance
      
      if (isPositive) {
        return {
          background: `linear-gradient(to right, transparent 49%, #d1d5db 49%, #d1d5db 51%, #bfdbfe 51%, ${gradientColor} ${51 + barWidth}%, transparent ${51 + barWidth}%)`,
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'right',
          paddingRight: '8px'
        };
      } else {
        return {
          background: `linear-gradient(to right, transparent ${49 - barWidth}%, ${gradientColor} ${49 - barWidth}%, #bfdbfe 49%, #d1d5db 49%, #d1d5db 51%, transparent 51%)`,
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'right',
          paddingRight: '8px'
        };
      }
    },
    valueFormatter: params => {
      const variance = parseFloat(params.value) || 0;
      return variance === 0 ? '0%' : (variance > 0 ? '+' : '') + variance.toFixed(1) + '%';
    }
  },
  { headerName: 'City', field: 'city', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 80, cellStyle: {fontSize: '12px'} },
  { headerName: 'State', field: 'state', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 50, cellStyle: {fontSize: '12px'} },
  { headerName: 'Primary Use Type', field: 'primary_building_use_type', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 100, cellStyle: {fontSize: '12px'} },
  { 
    headerName: 'Construction Type', 
    field: 'project_construction_types_s', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 100,
    valueFormatter: params => {
      if (Array.isArray(params.value) && params.value.length > 0) {
        return params.value[0];
      }
      return params.value || 'Not specified';
    },
    cellStyle: {fontSize: '12px'} 
  },
  { headerName: 'GSF', field: 'totalGSF', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 80, type: 'rightAligned', cellStyle: { fontWeight: '600', fontSize: '12px' } },
  { 
    headerName: 'Lender', 
    field: 'primary_project_lender', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 120,
    cellStyle: { fontSize: '12px', color: '#1f2937' },
    valueFormatter: params => params.value || '-'
  },
  { 
    headerName: 'Developer', 
    field: 'primary_project_developer', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 120,
    cellStyle: { fontSize: '12px', color: '#059669' },
    valueFormatter: params => params.value || '-'
  },
  { 
    headerName: 'General Contractor', 
    field: 'project_general_contractor', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 140,
    cellStyle: { fontSize: '12px', color: '#000000' },
    valueFormatter: params => params.value || '-'
  },
  { 
    headerName: 'Architect', 
    field: 'project_architect', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 120,
    cellStyle: { fontSize: '12px', color: '#000000' },
    valueFormatter: params => params.value || '-'
  },
  { 
    headerName: 'Structural Engineer', 
    field: 'project_structural_engineer', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 140,
    cellStyle: { fontSize: '12px', color: '#000000' },
    valueFormatter: params => params.value || '-'
  },
  { 
    headerName: 'MEP Engineer', 
    field: 'project_mep_engineer', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 120,
    cellStyle: { fontSize: '12px', color: '#000000' },
    valueFormatter: params => params.value || '-'
  },
  { 
    headerName: 'Civil Engineer', 
    field: 'project_civil_engineer', 
    resizable: false, 
    sortable: true, 
    filter: true, 
    floatingFilter: true, 
    minWidth: 120,
    cellStyle: { fontSize: '12px', color: '#000000' },
    valueFormatter: params => params.value || '-'
  },
  // Hidden columns that can be toggled
  { headerName: 'Address', field: 'address_line_1', resizable: false, sortable: true, filter: true, floatingFilter: true,  minWidth: 200, cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Asset Class', field: 'primary_asset_category', resizable: false, sortable: true, filter: true, floatingFilter: true, cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Actual Budget', field: 'total_revised', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 180, maxWidth: 180, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Planned Duration', field: 'plannedProjectMonths', resizable: false, sortable: true, filter: true, floatingFilter: true, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Actual Duration', field: 'actualProjectMonths', resizable: false, sortable: true, filter: true, floatingFilter: true, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Schedule Variance', field: 'scheduleVariance', resizable: false, sortable: true, filter: true, floatingFilter: true, type: 'rightAligned', valueFormatter: params => {
    return `${numberFormat(parseFloat(params.value))}`; 
    }, cellStyle: {fontSize: '12px'}, hide: true
  },
  { headerName: 'Planned Start', field: 'planned_start_date', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 150, maxWidth: 150, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Planned End', field: 'planned_end_date', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 150, maxWidth: 150, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Actual Start', field: 'actual_start_date', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 150, maxWidth: 150, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true },
  { headerName: 'Actual End', field: 'actual_end_date', resizable: false, sortable: true, filter: true, floatingFilter: true, minWidth: 150, maxWidth: 150, type: 'rightAligned', cellStyle: {fontSize: '12px'}, hide: true  },
])

function getTotals(assetCategory, filterRowsBy) {
  // Filter rowData.value based primary asset category
  const filteredRows = activeGridData.value.filter(row =>
    row.primary_asset_category?.includes(assetCategory)
  );

  // Map the filtered rows to their budget values - handle different field names
  const budgetValues = filteredRows.map(row => {
    let value = 0;
    
    if (filterRowsBy === 'total_revised') {
      // Use total_revised if available, otherwise fall back to total_original_budget
      value = parseFloat(row.total_revised || row.total_original_budget || 0);
    } else if (filterRowsBy === 'total_original') {
      // Use total_original_budget for original budget
      value = parseFloat(row.total_original_budget || 0);
    } else {
      // For any other field, use it directly
      value = parseFloat(row[filterRowsBy] || 0);
    }
    
    return value;
  });

  // Sum the values
  const totalSum = budgetValues.reduce((acc, currentValue) => acc + currentValue, 0);
  
  return totalSum;
}

// ❌ REMOVED extractTeamDataFromProject - No longer needed!
// ProjectSerializer already returns all team data:
// - primary_project_lender, primary_project_developer (from owner_allocations)
// - project_general_contractor (from contractor relationship)
// - project_architect, project_structural_engineer, project_mep_engineer, project_civil_engineer (from consultant_projects)

// Call all projects
async function callAllProjects() {
  let validIds = [];
  return new Promise(async (resolve) => {
    // ✅ USE OPTIMIZED PORTFOLIO ENDPOINT (batched queries, no N+1)
    axiosQuery(
      `${apiBase}portfolio/summary`,
      authToken,
      async (response) => {
        // Portfolio endpoint returns {projects: [...], metadata: {...}}
        const data = response.projects || response;
        rowData.value = data;

        rowData.value.forEach((row) => {
          validIds.push(row.id);
        });

        // ⚡ PERFORMANCE OPTIMIZATION: Reduce individual API calls
        console.log(`🚀 PERFORMANCE: Loading portfolio with ${rowData.value.length} projects...`);
        
        // Process each project to enhance data with team information
        const enhancedData = await Promise.all(rowData.value.map(async (row) => {
          // Define a helper function to safely parse date strings, returning null for invalid inputs
          const parseDateString = (dateString) => {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
          };

          // Convert date strings to Date objects
          const actualStartDate = parseDateString(row.actual_start_date);
          const actualEndDate = parseDateString(row.actual_end_date);

          const plannedStartDate = parseDateString(row.planned_start_date);
          const plannedEndDate = parseDateString(row.planned_end_date);

          // Calculate the difference in months between actualEndDate and actualStartDate
          const actualProjectMonths = actualEndDate && actualStartDate ?
            (actualEndDate.getFullYear() - actualStartDate.getFullYear()) * 12 + actualEndDate.getMonth() - actualStartDate.getMonth() :
            0;

          // Calculate the difference in months between plannedStartDate and plannedEndDate
          const plannedProjectMonths = plannedEndDate && plannedStartDate ?
            (plannedEndDate.getFullYear() - plannedStartDate.getFullYear()) * 12 + plannedEndDate.getMonth() - plannedStartDate.getMonth() :
            0;

          // Calculate scheduleVariance as percentage
          const scheduleVariance = plannedProjectMonths === 0 ? 0 : ((actualProjectMonths - plannedProjectMonths) / plannedProjectMonths) * 100;

          // ⚡ Use primary image from ProjectSerializer (already included!)
          let imageUrl = getPrimaryImageUrl(row);

          // ✅ Team data already in ProjectSerializer response (no additional processing needed!)

          // Calculate budget variance using ACTUAL extracted data from draws
          let budgetVariance = 0;
          try {
            const originalBudget = parseFloat(row.total_original_budget || 0);
            const currentBudget = parseFloat(row.total_current_budget || 0);
            
            // Only calculate variance if we have BOTH budgets from actual draw data
            if (originalBudget > 0 && currentBudget > 0) {
              budgetVariance = ((currentBudget - originalBudget) / originalBudget) * 100;
            }
            // If no draw data, budgetVariance stays 0 (will be filtered out of charts)
          } catch (error) {
            budgetVariance = 0;
          }

          // Ensure we have proper asset category classification
          let assetCategory = row.primary_asset_category;
          
          if (!assetCategory && row.primary_building_use_type) {
            // Map building use types to asset categories ONLY if we have building use data
            const useType = row.primary_building_use_type.toLowerCase();
            if (useType.includes('mixed') || useType.includes('mixed use') || useType.includes('mixed-use')) {
              assetCategory = 'Mixed Use';
            } else if (useType.includes('residential') || useType.includes('apartment') || useType.includes('condo')) {
              assetCategory = 'Residential';
            } else if (useType.includes('office') || useType.includes('commercial')) {
              assetCategory = 'Commercial';
            } else if (useType.includes('retail') || useType.includes('store') || useType.includes('shopping')) {
              assetCategory = 'Retail';
            } else if (useType.includes('industrial') || useType.includes('warehouse') || useType.includes('manufacturing')) {
              assetCategory = 'Industrial';
            } else if (useType.includes('hotel') || useType.includes('hospitality')) {
              assetCategory = 'Hospitality';
            } else if (useType.includes('civic') || useType.includes('cultural') || useType.includes('government') || useType.includes('education') || useType.includes('hospital')) {
              assetCategory = 'Civic & Cultural';
            }
            // NO DEFAULT FALLBACK - if we can't determine it, leave it null
          }
          
          // Override for project 397 if it should be Mixed Use
          if (row.id === 397 && assetCategory && !assetCategory.includes('Mixed')) {
            assetCategory = 'Mixed Use';
          }

          // Return the updated row object with enhanced data
          return {
            ...row,
            totalGSF: numberFormat(row.total_gsf),
            plannedProjectMonths: plannedProjectMonths,
            actualProjectMonths: actualProjectMonths,
            scheduleVariance: scheduleVariance,
            budgetVariance: budgetVariance,
            image_url: imageUrl,
            primary_asset_category: assetCategory, // Keep original value (can be null)
            // Team data
            primary_project_lender: row.primary_project_lender,
            primary_project_developer: row.primary_project_developer,
            project_general_contractor: row.project_general_contractor,
            project_architect: row.project_architect,
            project_structural_engineer: row.project_structural_engineer,
            project_mep_engineer: row.project_mep_engineer,
            project_civil_engineer: row.project_civil_engineer,
          };
        }));

        rowData.value = enhancedData;
        activeGridData.value = rowData.value;
        
        console.log(`✅ PERFORMANCE: Portfolio loaded ${enhancedData.length} projects successfully (no individual API calls)`);
        
        // Call to load pins in the map
        loadMarkers(enhancedData);
        isLoading.value = false;
        resolve(validIds);

      },
      errorStore,
      'Error', 'Error fetching projects'
    );
  })
}

function recalculateBudgetVariance() {
  isBudgetVarianceDataFetch.value = false;

  // Budget Variance
  // Step 1: Filter out entries with a budgetVariance of 0
  const filteredData = activeGridData.value.filter(row => row.budgetVariance !== 0 && row.budgetVariance !== null);
  
  // Step 2: Separate positive and negative budget variance entries
  const positiveDataBV = filteredData.filter(row => row.budgetVariance > 0);
  const negativeDataBV = filteredData.filter(row => row.budgetVariance < 0);
  
  // Step 3: Sort positive entries in descending order (most over) and take the top 3
  const top3PositiveDataBV = positiveDataBV
    .sort((a, b) => b.budgetVariance - a.budgetVariance)
    .slice(0, 3);
  
  // Step 4: Sort negative entries in ascending order (most under) and take the top 2
  const top2NegativeDataBV = negativeDataBV
    .sort((a, b) => a.budgetVariance - b.budgetVariance)
    .slice(0, 2);
  
  // Step 5: Combine the two arrays, with positive values first
  const combinedData = [...top3PositiveDataBV, ...top2NegativeDataBV];
  
  // Resetting the arrays
  bvStackedBarLabels.value = [];
  bvBudgetVarianceArr.value = [];
  
  // Step 6: Iterate over the combined array to populate bvStackedBarLabels and bvBudgetVarianceArr
  combinedData.forEach((row) => {
    bvStackedBarLabels.value.push(row.name);
    // Convert percentage (44.5) to decimal (0.445) for chart scale (max: 1)
    bvBudgetVarianceArr.value.push(row.budgetVariance / 100);
  });
  
  // Step 7: Find the maximum absolute value in the bvBudgetVarianceArr for scaling
  const varianceArr = bvBudgetVarianceArr.value.map(Math.abs);
  if (varianceArr.length === 0) {
    bvMaxValue.value = 0; // or some default value
  } else {
    bvMaxValue.value = Math.ceil(Math.max(...varianceArr));
  }

  // Step 8: Set the isBudgetVarianceDataFetch flag to true
  isBudgetVarianceDataFetch.value = true;
}

function recalculateScheduleVariance() {
  isScheduleVarianceDataFetch.value = false;

  // Step 1: Filter out entries with a scheduleVariance of 0 or null
  const filteredData = activeGridData.value.filter(row => row.scheduleVariance !== 0 && row.scheduleVariance !== null);
  
  // Step 2: Separate positive and negative schedule variance entries
  const positiveDataSV = filteredData.filter(row => row.scheduleVariance > 0);
  const negativeDataSV = filteredData.filter(row => row.scheduleVariance < 0);
  
  // Step 3: Sort positive entries in descending order (most over) and take the top 3
  const top3PositiveDataSV = positiveDataSV
    .sort((a, b) => b.scheduleVariance - a.scheduleVariance)
    .slice(0, 3);
  
  // Step 4: Sort negative entries in ascending order (most under) and take the top 2
  const top2NegativeDataSV = negativeDataSV
    .sort((a, b) => a.scheduleVariance - b.scheduleVariance)
    .slice(0, 2);
  
  // Step 5: Combine the two arrays, with positive values first
  const combinedData = [...top3PositiveDataSV, ...top2NegativeDataSV];

  // Resetting the arrays
  svStackedBarLabels.value = [];
  svScheduleVarianceArr.value = [];

  // Step 6: Iterate over the combined array to populate svStackedBarLabels and svScheduleVarianceArr
  combinedData.forEach((row) => {
    svStackedBarLabels.value.push(row.name);
    svScheduleVarianceArr.value.push(row.scheduleVariance);
  });

  // Step 7: Find the maximum absolute value in the svScheduleVarianceArr for scaling
  const varianceArr = svScheduleVarianceArr.value.map(Math.abs);
  if (varianceArr.length === 0) {
    svMaxValue.value = 0; // or some default value
  } else {
    svMaxValue.value = Math.ceil(Math.max(...varianceArr));
  }

  // Check if all values in the svScheduleVarianceArr array are zero
  svScheduleVarianceArrZeroValues.value = svScheduleVarianceArr.value.every(value => value === 0);

  // Step 8: Set the isScheduleVarianceDataFetch flag to true
  isScheduleVarianceDataFetch.value = true;
}

function drawAreaChart() {
  // Project Starts
  // Step 1 & 2: Extract years from actual_start_date and convert them
  const projectYears = activeGridData.value.map(row => new Date(row.actual_start_date).getFullYear());

  // New Step: Filter out 0 or null years
  const filteredProjectYears = projectYears.filter(year => year && year !== 1970);

  // Step 3: Count the number of projects per year, using filteredProjectYears
  const projectCountPerYear = filteredProjectYears.reduce((acc, year) => {
    acc[year] = (acc[year] || 0) + 1;
    return acc;
  }, {});

  // Step 4: Sort the years
  const sortedYears = Object.keys(projectCountPerYear).sort((a, b) => a - b);

  // Resetting the arrays
  projectStartsLineLabels.value = [];
  projectStartsLineData.value = [];

  // Step 5 & 6: Push the sorted years and their counts
  sortedYears.forEach(year => {
    projectStartsLineLabels.value.push(year);
    projectStartsLineData.value.push(projectCountPerYear[year]);
  });
}

function drawDonutChart() {
  // Get the total revised values
  residentialTotalRevisedBudget.value = getTotals('Residential', 'total_revised')
  commercialTotalRevisedBudget.value = getTotals('Commercial', 'total_revised')
  industrialTotalRevisedBudget.value = getTotals('Industrial', 'total_revised')
  hospitalityTotalRevisedBudget.value = getTotals('Hospitality', 'total_revised')
  retailTotalRevisedBudget.value = getTotals('Retail', 'total_revised')
  civicCulturalTotalRevisedBudget.value = getTotals('Civic & Cultural', 'total_revised')
  mixedUseTotalRevisedBudget.value = getTotals('Mixed Use', 'total_revised')

  residentialTotalOriginalBudget.value = getTotals('Residential', 'total_original')
  commercialTotalOriginalBudget.value = getTotals('Commercial', 'total_original')
  industrialTotalOriginalBudget.value = getTotals('Industrial', 'total_original')
  hospitalityTotalOriginalBudget.value = getTotals('Hospitality', 'total_original')
  retailTotalOriginalBudget.value = getTotals('Retail', 'total_original')
  civicCulturalTotalOriginalBudget.value = getTotals('Civic & Cultural', 'total_original')
  mixedUseTotalOriginalBudget.value = getTotals('Mixed Use', 'total_original')

  doughnutChartData.value = [
    residentialTotalRevisedBudget.value,
    commercialTotalRevisedBudget.value,
    industrialTotalRevisedBudget.value,
    hospitalityTotalRevisedBudget.value,
    retailTotalRevisedBudget.value,
    civicCulturalTotalRevisedBudget.value,
    mixedUseTotalRevisedBudget.value
  ]

  const doughnutChartDataArray = [
    residentialTotalRevisedBudget.value,
    commercialTotalRevisedBudget.value,
    industrialTotalRevisedBudget.value,
    hospitalityTotalRevisedBudget.value,
    retailTotalRevisedBudget.value,
    civicCulturalTotalRevisedBudget.value,
    mixedUseTotalRevisedBudget.value
]
  // Loop through the dougnutChartData
  const totalSumOfDoughnut = parseFloat(doughnutChartDataArray.reduce((a, b) => a + b, 0).toFixed(0));
  
  doughnutChartDataArray.forEach(val => {
    // todo: need to ask the team what should be the returned value if the sum is 0
    if (isNaN(totalSumOfDoughnut) || totalSumOfDoughnut === 0) {
      console.error('Invalid totalSumOfDoughnut:', totalSumOfDoughnut);
      return;
    }
    const percentage = val * 100 / totalSumOfDoughnut;
    const roundedPercentage = Math.round(percentage * 100) / 100;
    doughnutChartPercentage.value.push(`${roundedPercentage.toFixed(0)}%`);
  });

  isDoughnutChartDataLoaded.value = true
}

function drawVarianceCharts() {
  recalculateBudgetVariance();
  recalculateScheduleVariance();
}

// Get Initial Data for the Donut Chart
async function getDonutChartData(selectedIds) {
  let validRows = []
  return new Promise((resolve) => {
    axiosQuery(
      `${apiBase}/portfolio/summary/fetch_total_costs_per_project?project_ids=${selectedIds.join(',')}`,
      authToken,
      (data) => {
        // Donut data - DO NOT override the project budget fields with API data since API returns 0 values
        // Keep original project data intact
        activeGridData.value = rowData.value
        drawDonutChart()
        validRows = rowData.value;

        // Budget variance is already calculated in callAllProjects using correct project data
        // Do not recalculate using broken API data that returns 0 values

        resolve(validRows)
      },
      errorStore,
      'Error fetching doughnut chart data'
    );
  })
}

let isLoading = ref(true)

onMounted(async () => {
  // Set the authentication token first
  authToken.value = authStore.getAuthTokenHeader;
  
  // Ensure the DOM is fully updated before initializing the map
  // Timeout for loading Google Maps
  setTimeout(() => {
    // Load the Google Maps API and initialize the map
    loadGoogleMapsAPI.then(() => {
      loadGoogleMaps();
    }).catch(error => {
      // Handle error if Google Maps API fails to load
      console.error('Error loading Google Maps API:', error);
    });
  }, 250);

  try {
    // TODO: Optimize this part to avoid using awaits
    // Fetch all project IDs
    const selectedIds = await callAllProjects();
    // Fetch and display donut chart data based on selected project IDs
    await getDonutChartData(selectedIds);
  } catch (error) {
    console.error('Error loading project data:', error);
  }
});

// Toggle Columns
const agGridRef = ref(null);
const columnsToToggle = [
    'project_general_contractor',
    'project_architect',
    'total_original_budget',
    'total_revised',
    'plannedProjectMonths',
    'actualProjectMonths',
    'scheduleVariance',
    'planned_start_date',
    'planned_end_date',
    'actual_start_date',
    'actual_end_date'
  ];

let isGridExpanded = ref(false)
const toggleColumns = () => {
  isGridExpanded.value = !isGridExpanded.value
  agGridRef.value.toggleColumns();
};

</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

.portfolio-view-container {
  background: #f8fafc;
  padding: 20px;
  min-height: 100vh;
  margin-top: 60px;
  font-family: 'Inter';

  .breadcrumb-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    font-size: 1rem;
    color: #8C8D92;
  }

  .dashboard-three-column {
    display: flex;
    gap: 20px;
    margin-bottom: 30px;
    height: 500px;
  }

  .dashboard-tile {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 20px;
    display: flex;
    flex-direction: column;
    
    &.variance-and-starts-tile {
      padding: 15px; /* Reduced padding for variance tile */
    }
  }

  .map-tile {
    flex: 2; /* Map takes more space */
  }

  .doughnut-tile {
    flex: 1.2; /* Asset class chart is narrower */
  }

  .variance-and-starts-tile {
    flex: 2; /* Variance summary takes more space */
  }

  .map-tile {
    padding: 0;
    
    #myDataMap {
      height: 100%;
      border-radius: 12px;
    }
  }

  .doughnut-tile {
    display: flex;
    flex-direction: column;
    
    .doughnut-container {
      flex: 1;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 15px;
    }
    
    .doughnut-chart-wrapper {
      text-align: center;
      flex-shrink: 0;
      width: 220px;
      height: 220px;
    }
    
    .doughnut-chart-legend {
      font-size: 0.7rem;
      flex: 1;
      overflow-y: auto;
      margin-top: 20px;
      
      .legend-item {
        margin-bottom: 6px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .legend-label {
        font-size: 0.7rem;
        font-weight: 600;
        color: #374151;
        flex-shrink: 0;
        min-width: 70px;
      }
      
      .detail-container {
        display: flex;
        gap: 6px;
        align-items: center;
      }
      
      .percentage {
        font-size: 0.65rem;
        font-weight: 700;
        color: #1f2937;
        min-width: 30px;
      }
      
      .amount {
        font-size: 0.6rem;
        color: #6b7280;
      }
      
      .vertical-divider {
        width: 2px;
        height: 12px;
        border-radius: 2px;
      }
      
      &.residential-legend .vertical-divider { background: #52596D; }
      &.office-legend .vertical-divider { background: #4A5FD5; }
      &.industrial-legend .vertical-divider { background: #4A8FD0; }
      &.hospitality-legend .vertical-divider { background: #8A92A9; }
      &.retail-legend .vertical-divider { background: #d3d3d3; }
      &.civic-cultural-legend .vertical-divider { background: #6ED0CD; }
      &.mixed-use-legend .vertical-divider { background: #9B59B6; }
    }
    
    .modern-summary-grid {
      margin-top: 15px;
      flex-shrink: 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      
      .summary-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .summary-label, .summary-value {
          font-size: 0.7rem;
        }
        
        .summary-label {
          color: #6b7280;
        }
        
        .summary-value {
          font-weight: 700;
          color: #1f2937;
        }
      }
    }
  }

  .variance-and-starts-tile {
    display: flex;
    flex-direction: column;
    gap: 6px; /* Reduced gap to save space */
    height: 100%; /* Use full tile height */
    
    .variance-top-section {
      display: flex;
      gap: 10px;
      height: 180px; /* Back to original height */
      flex-shrink: 0;
    }

    .variance-chart {
      width: 50%; /* Fixed equal width instead of flex: 1 */
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      background: #f8f9fa;
      border-radius: 8px;
      padding: 8px;
      min-height: 0;
      
      .variance-subtitle {
        font-size: 0.8rem;
        font-weight: 600;
        color: #6B7280;
        margin-bottom: 6px;
        text-align: center;
        flex-shrink: 0;
      }
      
      .chart-container {
        flex: 1;
        min-height: 0;
        max-height: 140px; /* Back to original */
        
        canvas {
          max-height: 140px !important;
          width: 100% !important; /* Ensure charts fill their containers */
        }
      }
    }

    .project-starts-section {
      flex: 1; /* Takes remaining space - back to original */
      display: flex;
      flex-direction: column;
      background: #f8f9fa;
      border-radius: 8px;
      padding: 8px;
      min-height: 0; /* Allow shrinking */
      overflow: hidden; /* Prevent overflow */
      width: 100%; /* Full width of parent */
      
      .variance-subtitle {
        font-size: 0.8rem;
        font-weight: 600;
        color: #6B7280;
        margin-bottom: 6px;
        text-align: center;
        flex-shrink: 0;
      }
      
      .chart-container {
        flex: 1;
        min-height: 0;
        max-height: 240px; /* Back to original */
        width: 100%; /* Full width */
        
        canvas {
          max-height: 240px !important;
          width: 100% !important; /* Force full width */
        }
      }
    }
  }

  .dashboard-header,
  .projects-header {
    margin: 15px 0;
    display: flex;
    align-items: center;
  }

  .section-title-container {
    font-size: 1.5rem;
    font-weight: 600;
    color: $navyBlue;
    display: flex;
    margin-right: 10px;
  }

  .section-subtitle-container {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1rem;
    margin-right: 10px;
  }
}

// Modern Tile Styling
.modern-tile {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 24px;
  border: none;
  overflow: hidden;
  
  .modern-chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    .chart-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }
    
    .chart-badge {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 600;
      font-size: 1rem;
      min-width: 40px;
      text-align: center;
    }
  }
}



// Enhanced Doughnut Chart Styling
.doughnut-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  
  .doughnut-chart-wrapper {
    position: relative;
    flex-shrink: 0;
    
    
  }
  
  .doughnut-chart-legend {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
    overflow: hidden; // Prevent overflow
    
    .legend-item {
      min-height: 0; // Allow shrinking
      
      .legend-label {
        font-weight: 600;
        color: #374151;
        margin-bottom: 1px;
        font-size: 0.8rem;
        line-height: 1;
      }
      
      .detail-container {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: nowrap; // Prevent wrapping
        
        .percentage {
          font-weight: 700;
          color: #1f2937;
          min-width: 30px;
          font-size: 0.75rem;
          flex-shrink: 0;
        }
        
        .vertical-divider {
          width: 2px;
          height: 12px;
          border-radius: 2px;
          flex-shrink: 0;
        }
        
        .amount {
          color: #6b7280;
          font-size: 0.7rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0; // Allow shrinking
          max-width: 60px; // Limit width to prevent overflow
        }
      }
      
      &.residential-legend .vertical-divider { background: #3b82f6; }  /* Blue */
      &.office-legend .vertical-divider { background: #10b981; }       /* Green */
      &.industrial-legend .vertical-divider { background: #f59e0b; }   /* Orange */
      &.hospitality-legend .vertical-divider { background: #8b5cf6; }  /* Purple */
      &.retail-legend .vertical-divider { background: #ef4444; }       /* Red */
      &.civic-cultural-legend .vertical-divider { background: #06b6d4; } /* Cyan */
      &.mixed-use-legend .vertical-divider { background: #84cc16; }    /* Lime */
    }
  }
}

// Modern Summary Grid
.modern-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  
  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .summary-label {
      color: #6b7280;
      font-size: 0.875rem;
    }
    
    .summary-value {
      font-weight: 700;
      color: #1f2937;
      font-size: 1rem;
    }
  }
}

// Project Starts Chart Enhancements
.project-starts-container {
  .project-stats {
    display: flex;
    gap: 24px;
    
    .stat-item {
      text-align: right;
      
      .stat-label {
        font-size: 0.75rem;
        color: #6b7280;
        margin-bottom: 2px;
      }
      
      .stat-value {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1f2937;
      }
    }
  }
  
  .line-chart-wrapper {
    margin: 20px 0;
  }
  
  .chart-stats {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
    
    .stats-row {
      display: flex;
      justify-content: space-around;
      
      .stats-label {
        color: #6b7280;
        font-size: 0.875rem;
        font-weight: 500;
      }
    }
  }
}

// Variance Chart Wrappers
.variance-chart-wrapper {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  
  canvas {
    background: white;
    border-radius: 6px;
  }
}



// Keep original tile for map (unchanged)
.tile {
  background-color: $white1;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 5px 5px 5px 0;
  border-radius: 8px;
  display: inline-block;
  box-sizing: border-box;
}

// Legacy chart title (for backwards compatibility)
.chart-title {
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Inter';
  color: #1f2937;
  margin: 0;
}

.no-generated-sv-variance{
  height: 230px;
  width: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/** Document Action Button Style */
.expand-action-button {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  .action-name {
    font-size: 14px;
    color: $brightBlue1;
  }

  .cssCircle {
    margin-left: 5px;
  }
}

/** Custom Plus Sign inside a circle */
.cssCircle {
  margin-left: 5px;
  -webkit-border-radius: 999px;
  -moz-border-radius: 999px;
  border-radius: 999px;

  width: 20px;
  height: 20px;
  padding: 0px;

  background: $white1;
  border: 1px solid $navyBlue;
  color: $navyBlue;
  text-align: center;
  -webkit-transition: background 0.2s linear;
  -moz-transition: background 0.2s linear;
  -ms-transition: background 0.2s linear;
  -o-transition: background 0.2s linear;
  transition: background 0.2s linear;
  transition: color 0.2s linear;

  font: 20px Arial, sans-serif
}

.cssCircle:hover {
  background: $mediumBlue2;
  cursor: pointer;
}

.plusSign {
  line-height: 1em;
}
.minusSign {
  line-height: .88em;
}

.plusSign:hover {
  color: $white1;
  border: 1px solid $white1;
}

// Keep original styling to prevent layout breaks

// Allow images to pop out of cells on hover ONLY for thumbnail column
:deep(.ag-cell[col-id="thumbnail"]) {
  overflow: visible !important;
  z-index: 1;
}

:deep(.ag-cell[col-id="thumbnail"]:hover) {
  z-index: 9999;
}

// Toggle Switch Styling
.toggle-container {
  margin-left: .5rem;
  margin-right: 1rem;
  width: 40px;
  height: 20px;
  background-color: #ccc;
  border-radius: 20px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.circle {
  width: 15px;
  height: 15px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 2.5px;
  left: 3px;
  transition: transform 0.3s ease;
}

.move-right {
  transform: translateX(18px);
}

.toggle-container:hover {
  background-color: #bbb;
}

.toggle-container.active {
  background-color: #4A5FD5;
}

// Responsive Design
@media (max-width: 1800px) {
  .doughnut-chart-legend {
    gap: 5px !important;
    
    .legend-item {
      .legend-label {
        font-size: 0.85rem !important;
        margin-bottom: 1px !important;
      }
      
      .detail-container {
        gap: 5px !important;
        
        .percentage {
          font-size: 0.8rem !important;
          min-width: 32px !important;
        }
        
        .amount {
          font-size: 0.75rem !important;
        }
      }
    }
  }
}

@media (max-width: 1600px) {
  .doughnut-chart-legend {
    gap: 4px !important;
    
    .legend-item {
      .legend-label {
        font-size: 0.8rem !important;
        margin-bottom: 1px !important;
      }
      
      .detail-container {
        gap: 4px !important;
        
        .percentage {
          font-size: 0.75rem !important;
          min-width: 30px !important;
        }
        
        .amount {
          font-size: 0.7rem !important;
        }
      }
    }
  }
}

@media (max-width: 1400px) {
  .doughnut-chart-legend {
    gap: 3px !important;
    
    .legend-item {
      .legend-label {
        font-size: 0.75rem !important;
        margin-bottom: 1px !important;
      }
      
      .detail-container {
        gap: 3px !important;
        
        .percentage {
          font-size: 0.7rem !important;
          min-width: 28px !important;
        }
        
        .amount {
          font-size: 0.65rem !important;
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .portfolio-view-container {
    .map-charts-container {
      .variance-charts-container {
        .project-starts-container {
          flex: 1;
          min-width: 100%;
        }
      }
    }
  }
  
  .doughnut-chart-legend {
    gap: 2px !important;
    
    .legend-item {
      .legend-label {
        font-size: 0.7rem !important;
        margin-bottom: 1px !important;
      }
      
      .detail-container {
        gap: 2px !important;
        
        .percentage {
          font-size: 0.65rem !important;
          min-width: 26px !important;
        }
        
        .vertical-divider {
          height: 10px !important;
          width: 2px !important;
        }
        
        .amount {
          font-size: 0.6rem !important;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .portfolio-view-container {
    padding: 15px;
    
    .map-charts-container {
      .map-container,
      .doughnut-chart-container {
        width: 100%;
        margin-right: 0;
        margin-bottom: 20px;
      }
      
      .variance-charts-container {
        flex-direction: column;
        
        .variance-tile {
          min-width: 100%;
          max-width: none;
        }
      }
    }
    
    .doughnut-container {
      flex-direction: column;
      text-align: center;
      
      .doughnut-chart-wrapper {
        margin: 0 auto;
        width: 180px;
        height: 180px;
      }
      
      .doughnut-chart-legend {
        margin-top: 15px;
        gap: 2px !important;
        
        .legend-item {
          .legend-label {
            font-size: 0.7rem !important;
          }
          
          .detail-container {
            .percentage {
              font-size: 0.65rem !important;
              min-width: 25px !important;
            }
            
            .amount {
              font-size: 0.6rem !important;
            }
            
            .vertical-divider {
              height: 10px !important;
              width: 2px !important;
            }
          }
        }
      }
    }
    
    .modern-summary-grid {
      grid-template-columns: 1fr;
    }
    }
}

// Simple Colored Headers (No Gradients)
.simple-chart-header {
  margin: -24px -24px 15px -24px;
  padding: 15px 20px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .header-content {
    color: white;
    
    .header-title {
      font-size: 0.85rem;
      font-weight: 500;
      opacity: 0.9;
    }
    
    .header-highlight {
      font-size: 1.1rem;
      font-weight: 700;
    }
  }
  
  .chart-badge {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: white;
    padding: 6px 12px;
    border-radius: 15px;
    font-weight: 600;
    font-size: 0.9rem;
    min-width: 30px;
    text-align: center;
  }
}

.budget-header {
  background: #667eea; /* Blue like mockup */
}

.schedule-header {
  background: #718096; /* Grey like mockup */
}

// Enhanced Table Container - Mockup Colors
.enhanced-table-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-top: 20px;

  :deep(.ag-root-wrapper) {
    border-radius: 8px;
    overflow: hidden;
  }

  :deep(.ag-header) {
    background: #6b7db8 !important;
    
    .ag-header-cell,
    .ag-header-group-cell,
    .ag-header-cell-comp-wrapper,
    .ag-header-group-cell-with-group {
      border-bottom: none !important;
      border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
      font-weight: 600;
      color: white !important;
      background: #6b7db8 !important;
      
      .ag-header-cell-text,
      .ag-header-group-text {
        color: white !important;
      }

      &:last-child {
        border-right: none !important;
      }
    }
    
    .ag-header-row,
    .ag-header-row-column-group,
    .ag-header-row-column {
      background: #6b7db8 !important;
    }
    
    // Target any remaining header elements
    * {
      background: #6b7db8 !important;
      color: white !important;
    }
  }

  :deep(.ag-row:hover) {
    background-color: #f8fafc;
  }

  :deep(.ag-cell) {
    border-right: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    padding: 8px 12px;
    line-height: 1.4;
    word-wrap: break-word;
    white-space: normal;
    overflow: visible;
    min-height: 60px;
    
    // Project name column gets special treatment
    &:first-child {
      font-weight: 500;
      color: #1e293b;
      white-space: normal;
      word-break: break-word;
      align-items: flex-start;
      padding-top: 12px;
    }
  }
}

// Project Totals Section
.project-totals-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  
  .totals-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e2e8f0;
    
    &:last-child {
      border-bottom: none;
    }
    
    .total-label {
      font-weight: 500;
      color: #475569;
      font-size: 0.9rem;
    }
    
    .total-value {
      font-weight: 700;
      color: #1e293b;
      font-size: 0.9rem;
    }
  }
}

:deep(.ag-row) {
  background-color: #f8fafc;
  min-height: 60px; // Increase row height for better text display
  
  &:hover {
    background-color: #f1f5f9;
  }
}

:deep(.ag-cell) {
  border-right: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: normal; // Allow text wrapping
  overflow: visible;
  
  // Project name column specific styling
  &:first-child {
    font-weight: 500;
    color: #1e293b;
    max-width: none;
    white-space: normal;
    word-break: break-word;
  }
}

// Common tile header styling
.tile-header {
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  text-align: center;
  
  &.compact-header {
    padding: 6px 10px;
    margin-bottom: 8px;
    font-size: 0.85rem;
  }
  
  &.minimal-header {
    padding: 4px 8px;
    margin-bottom: 4px;
    font-size: 0.8rem;
  }
}

.chart-container,
.bar-chart-container {
  height: 250px;
  position: relative;
  
  canvas {
    max-height: 250px !important;
  }
}

.budget-header {
  background: #667eea;
}

.schedule-header {
  background: #718096;
}

.project-starts-container .tile-header {
  background: #4a90e2;
  color: white;
}

// Zoom wrapper for Portfolio view - default 75% zoom for better visibility on smaller screens
.portfolio-zoom-wrapper {
  zoom: 0.75;
  -moz-transform: scale(0.75);
  -moz-transform-origin: 0 0;
  width: 133.33%; // Compensate for 75% zoom (100 / 0.75)
}

@media (min-width: 1920px) {
  // Full size on large screens
  .portfolio-zoom-wrapper {
    zoom: 1;
    -moz-transform: scale(1);
    width: 100%;
  }
}
</style>